import { Icon } from '@iconify/react';
import { useRef, useState } from 'react';
import editFill from '@iconify/icons-eva/edit-fill';
import { useNavigate } from 'react-router-dom';
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill';
import markerPath from '@iconify/icons-mdi/map-marker-path';
import heartOutline from '@iconify/icons-mdi/heart-outline';
import heart from '@iconify/icons-mdi/heart';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';

// material
import { MenuItem, IconButton, ListItemIcon, ListItemText, Divider, Badge } from '@mui/material';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { reportTripsActions } from '../../store';
import { deleteFlight, deletePosition, editItem } from '../../fetch';
import updateFlightList from './updateFlightList';
import FlightAddFlag from './FlightListAddFlag';
import FlightListAddComment from './FlightListAddComment';
import { useTranslation } from '../../common/LocalizationProvider';
import FlightListTimeLineAlert from './FlighListTimelineAlert';
import FlightListTimeLinePoint from './FlightListTimelinePoint';
import MenuPopover from '../MenuPopover';
import FlightListRemove from './FlightListRemove';
import {
  selectAbonnementData,
  selectLastFreeFlightUser,
  selectSession,
  selectUsersArray
} from '../../createSelectorUtils';

// ----------------------------------------------------------------------

FlightListMoreMenu.propTypes = {
  deviceId: PropTypes.number.isRequired,
  groupId: PropTypes.number,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  reportTrip: PropTypes.object.isRequired
};

export default function FlightListMoreMenu({ deviceId, groupId, startTime, endTime, reportTrip }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const session = useSelector(selectSession);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};

  const abonnementData = useSelector(selectAbonnementData);
  const lastFreeFlightUser = useSelector(selectLastFreeFlightUser);

  const abonnement = abonnementData
    ? true
    : lastFreeFlightUser === null ||
      (reportTrip &&
        new Date(reportTrip.startTime).getTime() <= new Date(lastFreeFlightUser).getTime());

  const user = useSelector(selectUsersArray).find(
    (e) => e.id === reportTrip.driverUniqueId && session.id !== e.id
  ) || { privateAccount: false };

  const [isOpen, setIsOpen] = useState(false);

  const handleAddFavorite = async () => {
    const body = {
      id: reportTrip.affectationVolsId,
      deviceId: reportTrip.deviceId,
      piloteId: reportTrip.driverUniqueId,
      instructeurId: reportTrip.instructeurUniqueId,
      startTime: reportTrip.startTime,
      endTime: reportTrip.endTime,
      groupId: reportTrip.groupId,
      attributes: {},
      comment: reportTrip.commentAffectationVols,
      flag: reportTrip.flagAffectationVols,
      favorite: !reportTrip.favoriteAffectationVols,
      remarquableTrace: reportTrip.pointRemarquableTrace > 0
    };

    const response = await editItem('affectationVols', body.id, body);
    if (response.ok) {
      enqueueSnackbar(body.favorite ? t('FL_Menu_Favorite_Added') : t('FL_Menu_Favorite_Removed'), {
        variant: 'success'
      });
      dispatch(reportTripsActions.update(await updateFlightList(body)));
    } else {
      const error = await response.text();
      const message = error.slice(0, error.indexOf('-'));
      enqueueSnackbar(message, { variant: 'error' });
    }

    setIsOpen(false);
  };

  const handleAddRemarquableTrace = async () => {
    const body = {
      id: reportTrip.affectationVolsId,
      deviceId: reportTrip.deviceId,
      piloteId: reportTrip.driverUniqueId,
      instructeurId: reportTrip.instructeurUniqueId,
      startTime: reportTrip.startTime,
      endTime: reportTrip.endTime,
      groupId: reportTrip.groupId,
      attributes: {},
      comment: reportTrip.commentAffectationVols,
      flag: reportTrip.flagAffectationVols,
      favorite: reportTrip.favoriteAffectationVols,
      remarquableTrace: !(reportTrip.pointRemarquableTrace > 0)
    };

    const response = await editItem('affectationVols', body.id, body);
    if (response.ok) {
      enqueueSnackbar(
        body.favorite
          ? t('FL_Menu_Remarquable_trace_Added')
          : t('FL_Menu_Remarquable_trace_Removed'),
        {
          variant: 'success'
        }
      );
      dispatch(reportTripsActions.update(await updateFlightList(body)));
    } else {
      const error = await response.text();
      const message = error.slice(0, error.indexOf('-'));
      enqueueSnackbar(message, { variant: 'error' });
    }

    setIsOpen(false);
  };

  const [openRemoveFlight, setOpenRemoveFlight] = useState(false);

  const handleDeleteFlight = async () => {
    const response = await deleteFlight(deviceId, startTime, endTime);

    if (response.ok) {
      enqueueSnackbar(t('FL_Menu_Snackbar_Delete_Flight'), { variant: 'success' });
      dispatch(reportTripsActions.delete({ deviceId, startTime, endTime }));
      setIsOpen(false);
    } else {
      response.text().then((error) => {
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      });
    }
    setOpenRemoveFlight(false);
  };

  const [openRemovePosition, setOpenRemovePosition] = useState(false);

  const handleDeletePosition = async () => {
    const response = await deletePosition(
      deviceId,
      reportTrip.startPositionId,
      reportTrip.endPositionId
    );

    if (response.ok) {
      enqueueSnackbar(t('FL_Menu_Snackbar_Delete_Position'), { variant: 'success' });
      dispatch(reportTripsActions.delete({ deviceId, startTime, endTime }));
      setIsOpen(false);
    } else {
      response.text().then((error) => {
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      });
    }
    setOpenRemovePosition(false);
  };

  return (
    <>
      <IconButton ref={ref} onClick={() => setIsOpen(true)}>
        <Icon icon={moreVerticalFill} width={20} height={20} />
      </IconButton>

      <MenuPopover
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        sx={{ width: 200 }}
      >
        <MenuItem
          onClick={() =>
            navigate('/flight/review', {
              state: {
                deviceId,
                groupId,
                startTime,
                endTime,
                privateAccount:
                  user.privateAccount &&
                  !session.administrator &&
                  session.id !== reportTrip.driverUniqueId &&
                  session.id !== reportTrip.instructeurUniqueId
              },
              replace: true
            })
          }
          sx={{ color: 'text.secondary' }}
          disabled={!(reportTrip.affectationVolsId !== 0 || session.administrator || groupId === 0)}
        >
          <ListItemIcon>
            <Icon icon={markerPath} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('FL_Menu_Review')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>
        <MenuItem
          disabled={
            reportTrip.affectationVolsId !== 0 &&
            permission.status !== 0 &&
            permission.status !== 1 &&
            !session.administrator
          }
          onClick={() =>
            navigate('/flight/assign', {
              state: { deviceId, groupId, startTime, endTime },
              replace: true
            })
          }
          sx={{ color: 'text.secondary' }}
        >
          <ListItemIcon>
            <Icon icon={editFill} width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={t('FL_Menu_Assign')}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <FlightListAddComment
            reportTrip={reportTrip}
            disabled={reportTrip.affectationVolsId === 0 || !abonnement}
          />
        </Badge>
        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <FlightAddFlag
            reportTrip={reportTrip}
            disabled={reportTrip.affectationVolsId === 0 || !abonnement}
          />
        </Badge>
        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <MenuItem
            onClick={handleAddFavorite}
            sx={{ color: 'text.secondary' }}
            disabled={reportTrip.affectationVolsId === 0 || !abonnement}
          >
            <ListItemIcon>
              <Icon
                icon={reportTrip.favoriteAffectationVols ? heart : heartOutline}
                width={24}
                height={24}
              />
            </ListItemIcon>
            <ListItemText
              primary={t('FL_Menu_Favorite')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        </Badge>
        {session.administrator && (
          <MenuItem
            onClick={handleAddRemarquableTrace}
            sx={{ color: 'text.secondary' }}
            disabled={reportTrip.affectationVolsId === 0}
          >
            <ListItemIcon>
              <Icon
                icon="icon-park-solid:in-flight"
                width={24}
                height={24}
                color={reportTrip.pointRemarquableTrace > 0 ? '#00561B' : '#bb0b0b'}
              />
            </ListItemIcon>
            <ListItemText
              primary={t('Point_Remarquable_Trace')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        {session.administrator && (
          <MenuItem sx={{ color: 'text.secondary' }} onClick={() => setOpenRemoveFlight(true)}>
            <ListItemIcon>
              <Icon icon={trash2Outline} width={24} height={24} />
            </ListItemIcon>
            <ListItemText
              primary={t('FL_Menu_Delete_Flight')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        {session.administrator && (
          <MenuItem sx={{ color: 'text.secondary' }} onClick={() => setOpenRemovePosition(true)}>
            <ListItemIcon>
              <Icon icon={trash2Outline} width={24} height={24} />
            </ListItemIcon>
            <ListItemText
              primary={t('FL_Menu_Delete_Position')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        <Divider />
        <FlightListTimeLinePoint reportTrip={reportTrip} />

        <Divider />
        <FlightListTimeLineAlert reportTrip={reportTrip} />
      </MenuPopover>
      <FlightListRemove
        open={openRemoveFlight || openRemovePosition}
        handleClose={() =>
          openRemovePosition ? setOpenRemovePosition(false) : setOpenRemoveFlight(false)
        }
        handleDelete={openRemovePosition ? handleDeletePosition : handleDeleteFlight}
      />
    </>
  );
}
